import { BaseCard, SkeletonLoader } from "@pepperstone/pepper-ui-kit";
import clsx from "clsx";
import type React from "react";
import { useTranslation } from "react-i18next";
import type {
  CashierTradingAccount,
  CalculatedTransactions,
} from "@/api/cashier";
import { usePlatformIcon } from "@/components/account-list/usePlatformIcon";
import { useFormatAmount } from "@/hooks/useFormatAmount";
import styles from "./SummaryCard.module.css";
import { moneyToMajorUnits } from "@/utils/helpers";

type SummaryCardProps = {
  selectedAccount: CashierTradingAccount;
  isLoading: boolean;
  transactions: CalculatedTransactions | undefined;
  children?: React.ReactNode;
};

export const SummaryCard = ({
  selectedAccount,
  isLoading,
  transactions,
  children,
}: SummaryCardProps) => {
  const { t } = useTranslation();
  const { formatAmount } = useFormatAmount();
  const icon = usePlatformIcon(selectedAccount.tradingPlatform);
  const currency = selectedAccount.currency;
  return (
    <BaseCard>
      <BaseCard.Content>
        <div className="ps-flex ps-flex-col ps-gap-4 ps-px-2">
          <div className="ps-flex ps-gap-2 ps-flex ps-items-center">
            <div className={styles.iconContainer}>
              <img alt="Trading Platform icon" src={icon} />
            </div>
            <div className={styles.textSecondary}>
              {`${selectedAccount.tradingPlatform} - ${selectedAccount.accountLoginId} - ${selectedAccount.accountType} - ${selectedAccount.accountNickname ?? selectedAccount.accountName}`}
            </div>
          </div>
          <div className="ps-flex ps-gap-2 ps-justify-between ps-pb-2">
            <div>
              <div className={styles.textSecondary}>
                {t("withdrawals.summary.summary_card.available_withdrawals")}
              </div>
              <div className={styles.textPrimary}>
                {isLoading ? (
                  <SkeletonLoader width={12} height={5} variant="rectangular" />
                ) : (
                  formatAmount(
                    moneyToMajorUnits(transactions?.remaining_money),
                    currency
                  )
                )}
              </div>
            </div>
            <div>
              <div className={clsx(styles.textSecondary, "ps-text-right")}>
                {t("withdrawals.summary.summary_card.total_amount")}
              </div>
              <div className={clsx(styles.textPrimary, "ps-text-right")}>
                {isLoading ? (
                  <SkeletonLoader width={12} height={5} variant="rectangular" />
                ) : (
                  formatAmount(
                    moneyToMajorUnits(transactions?.total_money),
                    currency
                  )
                )}
              </div>
            </div>
          </div>
          {children}
        </div>
      </BaseCard.Content>
    </BaseCard>
  );
};
